/**
 * ADAPTER (host) que implementa el puerto `VariableSchemaProvider` del engine.
 *
 * Traduce el catálogo de variables de NEGOCIACIONES (registry tipado + colecciones +
 * escalares del binding) al modelo PURO `VariableSchema` que consume el editor. Es la
 * ÚNICA fuente de verdad que después leen el menú `{`, el validador y el preview.
 *
 * Para agregar/editar una variable escalar del dominio: tocá su `defineVariable` en
 * `shared/variables/domains/*` (sigue siendo la fuente). Acá solo se ADAPTA la forma.
 */

import type { VariableSchemaProvider } from '@/document-engine/application/ports/variable-schema-provider'
import { MOCK_NEGOTIATION_VARIABLES } from '@/features/negotiations/utils/mock-variables-data'
import {
  CLIENTES_COLLECTION,
  type CollectionCatalog
} from '@/shared/variables/collections/clientes.collection'
import { CUOTAS_COLLECTION } from '@/shared/variables/collections/cuotas.collection'
import type { NegotiationVariable } from '@/shared/variables/core/define'
import { ALL_VARIABLES } from '@/shared/variables/registry'
import type {
  CollectionSchema,
  ScalarGroup,
  VariableSchema,
  VariableSchemaField
} from '@/shared/variables/variable-schema'

// Valores de muestra del dominio (moneda, fechas, etc.) para la previsualización en
// vivo del editor. Es data de demo del HOST — por eso vive en el binding, no en el engine.
const MOCK = MOCK_NEGOTIATION_VARIABLES as unknown as Record<string, string>

// Etiquetas legibles por categoría del registry (sin emojis — para el menú del engine).
const CATEGORY_LABEL: Record<string, string> = {
  society: 'Sociedad',
  project: 'Proyecto',
  property: 'Inmueble',
  negotiation: 'Negociación',
  currency: 'Moneda',
  owner: 'Propietario',
  advisor: 'Asesor',
  document: 'Documento',
  other: 'Otros'
}

// Variable del registry (VariableDef) → campo del esquema del engine.
const toField = (v: NegotiationVariable): VariableSchemaField => ({
  token: v.key,
  label: v.label,
  type: v.type,
  group: v.group,
  enumValues: v.enumValues,
  description: v.description,
  example: v.example,
  masks: v.masks
})

// Colección curada (CollectionCatalog) → colección del esquema del engine.
// El `token` del campo es la clave CORTA (ej. 'nombre'); el engine compone `alias.token`.
const toCollection = (c: CollectionCatalog): CollectionSchema => ({
  name: c.name,
  alias: c.defaultAlias,
  label: c.label,
  fields: c.fields.map((f) => ({
    token: f.key,
    label: f.label,
    type: f.type,
    enumValues: f.values,
    masks: f.mask ? [f.mask] : undefined
  }))
})

// Agrupa escalares por categoría. ÚNICA fuente: el registry tipado (incluye
// representante.* y parte_compradora_* migrados desde el viejo binding-scalars).
const buildScalarGroups = (): ScalarGroup[] => {
  const byCat = new Map<string, VariableSchemaField[]>()
  const push = (cat: string, field: VariableSchemaField): void => {
    const list = byCat.get(cat) ?? []
    list.push(field)
    byCat.set(cat, list)
  }
  for (const v of ALL_VARIABLES) push(CATEGORY_LABEL[v.category] ?? v.category, toField(v))
  return [...byCat.entries()].map(([label, fields]) => ({ category: label, label, fields }))
}

// Valores de muestra para la previsualización en vivo: ejemplos de escalares + el
// primer item de cada colección bajo su alias (ej. `cliente.nombre`).
const buildPreviewValues = (): Record<string, string> => {
  const out: Record<string, string> = { ...MOCK } // base de muestra del dominio (moneda, etc.)
  for (const v of ALL_VARIABLES) if (v.example) out[v.key] = v.example
  for (const c of [CLIENTES_COLLECTION, CUOTAS_COLLECTION]) {
    const sample = c.demo[0] ?? {}
    for (const f of c.fields) out[`${c.defaultAlias}.${f.key}`] = sample[f.key] ?? ''
  }
  return out
}

const SCHEMA: VariableSchema = {
  scalars: buildScalarGroups(),
  collections: [toCollection(CLIENTES_COLLECTION), toCollection(CUOTAS_COLLECTION)],
  // Tokens legacy `*_letras` que ya no se generan pero existen en plantillas guardadas.
  legacyTokens: [
    'estructura_numero_letras',
    'identificador_letras',
    'parqueo_numero_letras',
    'parqueo_vehiculos_letras'
  ],
  previewValues: buildPreviewValues()
}

export const negotiationVariableSchemaProvider: VariableSchemaProvider = {
  getSchema: () => SCHEMA
}
